import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Tab, Tabs } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../../../contexts/AuthContext';
import { useManagerProductionPermissions } from '../../manager/useManagerProductionPermissions';
import {
  ProductionNavProvider,
  type ProductionNav,
  type ProductionNewParams,
} from '../context/ProductionNavContext';
import { ProductionPermissionsProvider } from '../context/ProductionPermissionsContext';
import ProductionOrderEditor from './ProductionOrderEditor';
import ProductionOrders from './ProductionOrders';
import ProductionReports from './ProductionReports';
import ProductionDefinitions from './ProductionDefinitions';
import ProductionTree from './ProductionTree';
import ProductionWorkshop from './ProductionWorkshop';
import ProductionPlanning from './ProductionPlanning';

type ManagerProductionView =
  | { kind: 'list' }
  | { kind: 'new'; params?: ProductionNewParams }
  | { kind: 'order'; id: string }
  | { kind: 'tree'; productId?: string }
  | { kind: 'reports' }
  | { kind: 'definitions' }
  | { kind: 'workshop' }
  | { kind: 'planning' };

interface ManagerProductionPanelProps {
  branchId: string;
}

const ManagerProductionPanel: React.FC<ManagerProductionPanelProps> = ({ branchId }) => {
  const perms = useManagerProductionPermissions(true);
  const { isModuleEnabled } = useAuth();
  const opsVisible = isModuleEnabled('production_ops');
  const [stack, setStack] = useState<ManagerProductionView[]>([{ kind: 'list' }]);
  const view = stack[stack.length - 1];

  const push = useCallback((next: ManagerProductionView) => {
    setStack((current) => [...current, next].slice(-10));
  }, []);

  const nav = useMemo<ProductionNav>(() => ({
    openList: () => setStack([{ kind: 'list' }]),
    openNew: (params) => push({ kind: 'new', params }),
    openOrder: (id) => {
      setStack((current) => {
        const top = current[current.length - 1];
        const next: ManagerProductionView = { kind: 'order', id };
        if (top?.kind === 'new') return [...current.slice(0, -1), next].slice(-10);
        return [...current, next].slice(-10);
      });
    },
    openTree: (productId) => push({ kind: 'tree', productId }),
    openWorkshop: () => {
      if (perms.steps && opsVisible) push({ kind: 'workshop' });
    },
    newParams: view.kind === 'new' ? (view.params ?? null) : null,
    canLinkOutside: false,
  }), [push, view, perms.steps, opsVisible]);

  const rootKind = stack[0]?.kind === 'tree'
    ? 'tree'
    : stack[0]?.kind === 'reports'
      ? 'reports'
      : stack[0]?.kind === 'definitions'
        ? 'definitions'
        : stack[0]?.kind === 'planning'
          ? 'planning'
          : 'orders';
  const root = (rootKind === 'definitions' && !opsVisible) || (rootKind === 'planning' && !perms.planning)
    ? 'orders'
    : rootKind;

  const selectRoot = (next: 'orders' | 'tree' | 'reports' | 'definitions' | 'planning') => {
    if (next === 'planning' && perms.planning) setStack([{ kind: 'planning' }]);
    else if (next === 'tree') setStack([{ kind: 'tree' }]);
    else if (next === 'reports') setStack([{ kind: 'reports' }]);
    else if (next === 'definitions') setStack([{ kind: 'definitions' }]);
    else setStack([{ kind: 'list' }]);
  };

  if (!perms.loaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!perms.canView) {
    return <Alert severity="warning" sx={{ m: 2 }}>Üretim için yetkiniz yok.</Alert>;
  }

  return (
    <ProductionPermissionsProvider value={{ ...perms, branchId }}>
      <ProductionNavProvider value={nav}>
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            {stack.length > 1 ? (
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => setStack((current) => (current.length > 1 ? current.slice(0, -1) : current))}
              >
                Geri
              </Button>
            ) : null}
            <Tabs value={root} onChange={(_, next: 'orders' | 'tree' | 'reports' | 'definitions' | 'planning') => selectRoot(next)}>
              <Tab value="orders" label="Fişler" />
              {perms.planning ? <Tab value="planning" label="Planlama" /> : null}
              <Tab value="tree" label="Ürün Ağacı" />
              {perms.canReports ? <Tab value="reports" label="Raporlar" /> : null}
              {opsVisible ? <Tab value="definitions" label="Tanımlar" /> : null}
            </Tabs>
          </Box>
          {view.kind === 'list' ? <ProductionOrders /> : null}
          {view.kind === 'planning' && perms.planning ? <ProductionPlanning /> : null}
          {view.kind === 'new' ? <ProductionOrderEditor /> : null}
          {view.kind === 'order' ? <ProductionOrderEditor key={view.id} orderId={view.id} /> : null}
          {view.kind === 'tree' ? (
            <ProductionTree key={view.productId ?? 'tree'} initialProductId={view.productId} />
          ) : null}
          {view.kind === 'reports' && perms.canReports ? <ProductionReports /> : null}
          {view.kind === 'workshop' && opsVisible && perms.steps ? <ProductionWorkshop /> : null}
          {view.kind === 'definitions' && opsVisible ? (
            <ProductionDefinitions readOnly={!perms.opsSetup} managerBranchId={branchId} />
          ) : null}
        </Box>
      </ProductionNavProvider>
    </ProductionPermissionsProvider>
  );
};

export default ManagerProductionPanel;
